import React, { useState, useRef, useEffect } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import BackButton from './BackButton';

interface SMSCodeInputProps {
  phone: string;
  onSubmit: (code: string) => void;
  onResend: () => void;
  isLoading: boolean;
  error: string | null;
}

const SMSCodeInput: React.FC<SMSCodeInputProps> = ({
  phone,
  onSubmit,
  onResend,
  isLoading,
  error
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) return; // Предотвращаем вставку длинных значений
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Автоматический переход к следующему полю
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Если код заполнен полностью, отправляем его
    if (newCode.every(digit => digit !== '') && index === 5) {
      onSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Переход к предыдущему полю при удалении
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleResend = () => {
    onResend();
    setResendTimer(60); // 60 секунд до возможности повторной отправки
  };

  const formatPhone = (phone: string) => {
    // Форматирование номера телефона для отображения
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `+${cleaned[0]} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`;
    }
    return phone;
  };

  // Мобильная версия
  if (isMobile) {
    return (
      <div className="w-full max-w-sm mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {/* Back Button */}
          <div className="mb-6">
            <BackButton 
              onClick={() => window.history.back()} 
              text="Вернуться к форме входа"
              className="text-gray-600 hover:text-gray-800"
            />
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Введите код из SMS</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              Код отправлен на номер{' '}
              <span className="font-semibold text-gray-800">{formatPhone(phone)}</span>
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            </div>
          )}

          <div className="mb-8">
            <div className="flex justify-center space-x-2 mb-6">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-10 h-10 text-center text-lg font-bold border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all duration-200 bg-gray-50 hover:bg-white"
                  disabled={isLoading}
                />
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <button
              onClick={() => onSubmit(code.join(''))}
              disabled={isLoading || code.some(digit => digit === '')}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Проверяем...
                </div>
              ) : (
                'Подтвердить'
              )}
            </button>

            <div className="text-center">
              {resendTimer > 0 ? (
                <div className="text-sm text-gray-500">
                  <span>Повторная отправка через </span>
                  <span className="font-semibold text-gray-700">{resendTimer}</span>
                  <span> сек</span>
                </div>
              ) : (
                <button
                  onClick={handleResend}
                  disabled={isLoading}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Отправить код повторно
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Десктопная версия (в стиле DesktopLogin)
  return (
    <div className="min-h-screen bg-[#141826] flex">
      {/* Left Panel - Logo and Info */}
      <div className="w-1/2 bg-gradient-to-br from-[#1a1f2e] to-[#141826] flex flex-col justify-center items-center p-12">
        <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-[#7661d7] rounded-full flex items-center justify-center mb-8">
            <span className="text-white text-4xl">A</span>
          </div>
          <h1 className="text-white text-5xl mb-4">Сделка и Точка</h1>
          <p className="text-slate-400 text-lg leading-relaxed mb-8">
            Безопасная авторизация через SMS код
          </p>
          
          {/* Phone Info */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 border border-white/10">
            <h3 className="text-white text-2xl mb-4">Подтверждение номера</h3>
            <div className="text-center">
              <p className="text-white/70 text-base mb-2">Код отправлен на номер:</p>
              <p className="text-white text-xl">{formatPhone(phone)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - SMS Code Form */}
      <div className="w-1/2 bg-[#141826] flex items-center justify-center p-12">
        <div className="w-full max-w-md">
          {/* Back Button */}
          <div className="mb-6">
            <BackButton 
              onClick={() => window.history.back()} 
              text="Вернуться к форме входа"
              className="text-white/70 hover:text-white"
            />
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-white text-3xl mb-2">Введите код из SMS</h2>
            <p className="text-slate-400 text-base">
              Введите 6-значный код, отправленный на ваш номер
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-300 rounded-2xl text-sm">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            </div>
          )}

          {/* SMS Code Input */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 mb-6">
            <div className="flex justify-center space-x-3 mb-8">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-12 text-center text-xl font-bold border-2 border-white/20 rounded-xl focus:border-white/40 focus:ring-2 focus:ring-white/10 focus:outline-none transition-all duration-200 bg-white/5 text-white placeholder-white/30 hover:bg-white/10"
                  placeholder="•"
                  disabled={isLoading}
                />
              ))}
            </div>

            {/* Submit Button */}
            <button
              onClick={() => onSubmit(code.join(''))}
              disabled={isLoading || code.some(digit => digit === '')}
              className={`w-full py-4 px-6 rounded-full flex items-center justify-center space-x-3 transition-all duration-200 ${
                isLoading || code.some(digit => digit === '')
                  ? 'bg-white/10 text-white/50 cursor-not-allowed'
                  : 'bg-white text-gray-800 hover:bg-white/90 active:scale-95'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-800 border-l-2 border-gray-800/20"></div>
                  <span>Проверяем...</span>
                </>
              ) : (
                <span>Подтвердить</span>
              )}
            </button>
          </div>

          {/* Resend Code */}
          <div className="text-center">
            {resendTimer > 0 ? (
              <div className="text-slate-400 text-sm">
                <span>Повторная отправка через </span>
                <span className="font-semibold text-white">{resendTimer}</span>
                <span> сек</span>
              </div>
            ) : (
                             <button
                 onClick={handleResend}
                 disabled={isLoading}
                 className="text-white/70 hover:text-white text-sm font-medium transition-colors disabled:text-white/30 disabled:cursor-not-allowed"
               >
                 Отправить код повторно
               </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSCodeInput;
